import { cloneDeep, isEqual, isPlainObject } from "lodash";

import { METADATA_KEY } from "../../constants";
import { IAuditLogDto, IAuditLogProperties } from "../../dto";
import { AuditLogEvents } from "../../enums";
import {
  AUDIT_LOG_DIFF_VERSION,
  AUDIT_LOG_ID_COLLECTIONS,
  AUDIT_LOG_ID_LIST_KEY_SUFFIX,
  AUDIT_LOG_NUMERIC_KEY_REGEX,
  AUDIT_LOG_ORDERED_ITEM_COLLECTIONS,
} from "./audit-log.constants";
import type {
  IAuditLogIdCollectionConfig,
  TAuditLogItemLabels,
  TAuditLogItemLabelSource,
  TAuditLogRecord,
} from "./audit-log.models";

/** `{"1": 999}`: how rows written before the current diff version patched an array by index. */
export const isAuditLogIndexPatch = (
  value: unknown,
): value is TAuditLogRecord => {
  if (!isPlainObject(value)) {
    return false;
  }
  const keys = Object.keys(value as TAuditLogRecord);
  return (
    keys.length > 0 && keys.every(key => AUDIT_LOG_NUMERIC_KEY_REGEX.test(key))
  );
};

const isAuditLogId = (value: unknown): value is number | string =>
  typeof value === "number" || (typeof value === "string" && value !== "");

const isAuditLogItemWithId = (
  item: unknown,
): item is { id: number | string } & TAuditLogRecord =>
  isPlainObject(item) && isAuditLogId((item as TAuditLogRecord).id);

const isLegacyAuditLogDiff = (diffVersion?: number): boolean =>
  diffVersion !== undefined && diffVersion < AUDIT_LOG_DIFF_VERSION;

/** The diff format of an update row; rows written before the marker existed are version 0. */
export const getAuditLogRowDiffVersion = (
  auditLog: Pick<IAuditLogDto, "metadata">,
): number => auditLog.metadata?.diffVersion ?? 0;

const applyPatchToRecord = ({
  state,
  patch,
  isTopLevel,
  diffVersion,
}: {
  state: TAuditLogRecord;
  patch: TAuditLogRecord;
  isTopLevel: boolean;
  diffVersion?: number;
}): TAuditLogRecord => {
  const next = { ...state };

  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) {
      continue;
    }
    // Inside jsonb a null marks a removed key; at the top level it is a cleared column.
    if (value === null && !isTopLevel) {
      delete next[key];
      continue;
    }
    next[key] = applyAuditLogPatchValue({
      current: next[key],
      patch: value,
      diffVersion,
    });
  }

  return next;
};

/**
 * Rows written before the current diff version patch an array by index. The old backend turned an index patch whose keys run
 * from 0 into a real array (`[{ boardingStatus }]` for `{"0": { boardingStatus }}`), so in those rows an array laid
 * over an array is a patch too, not the new value.
 */
const isAuditLogArrayIndexPatch = ({
  current,
  patch,
  diffVersion,
}: {
  current: unknown;
  patch: unknown;
  diffVersion?: number;
}): boolean =>
  Array.isArray(current) &&
  (isAuditLogIndexPatch(patch) ||
    (isLegacyAuditLogDiff(diffVersion) && Array.isArray(patch)));

/** In the current diff version an array of items with ids holds one entry per item, merged onto the previous one by id. */
const isAuditLogIdKeyedArrayPatch = ({
  current,
  patch,
  diffVersion,
}: {
  current: unknown;
  patch: unknown;
  diffVersion?: number;
}): boolean =>
  (diffVersion ?? 0) >= AUDIT_LOG_DIFF_VERSION &&
  Array.isArray(current) &&
  Array.isArray(patch) &&
  current.every(isAuditLogItemWithId) &&
  patch.every(isAuditLogItemWithId);

/**
 * The value after one audit row's patch: arrays are replaced, objects merged key by key, index patches applied by
 * index, id-keyed item patches merged by id. `diffVersion` is the row's diff format.
 */
export const applyAuditLogPatchValue = ({
  current,
  patch,
  diffVersion,
}: {
  current: unknown;
  patch: unknown;
  diffVersion?: number;
}): unknown => {
  if (isAuditLogIdKeyedArrayPatch({ current, patch, diffVersion })) {
    const previousById = new Map(
      (current as TAuditLogRecord[]).map(item => [String(item.id), item]),
    );
    // The patch lists the items in their new order; an item left out of it was removed.
    return (patch as TAuditLogRecord[]).map(item => {
      const previousItem = previousById.get(String(item.id));
      return previousItem
        ? applyAuditLogPatchValue({
            current: previousItem,
            patch: item,
            diffVersion,
          })
        : cloneDeep(item);
    });
  }

  if (isAuditLogArrayIndexPatch({ current, patch, diffVersion })) {
    const next = [...(current as unknown[])];
    for (const [index, value] of Object.entries(
      patch as TAuditLogRecord | unknown[],
    )) {
      // An old diff marked a dropped item as undefined (null once stored); it cannot tell which one, so keep it.
      if (
        isLegacyAuditLogDiff(diffVersion) &&
        (value === null || value === undefined)
      ) {
        continue;
      }
      next[Number(index)] = applyAuditLogPatchValue({
        current: next[Number(index)],
        patch: value,
        diffVersion,
      });
    }
    return next;
  }

  if (isPlainObject(current) && isPlainObject(patch)) {
    return applyPatchToRecord({
      state: current as TAuditLogRecord,
      patch: patch as TAuditLogRecord,
      isTopLevel: false,
      diffVersion,
    });
  }

  return cloneDeep(patch);
};

/** Replays one audit row onto the entity state built from the rows before it. */
export const applyAuditLogPatch = ({
  state,
  patch,
  diffVersion,
}: {
  state: IAuditLogProperties;
  patch: IAuditLogProperties;
  diffVersion?: number;
}): IAuditLogProperties =>
  applyPatchToRecord({
    state: state as TAuditLogRecord,
    patch: patch as TAuditLogRecord,
    isTopLevel: true,
    diffVersion,
  }) as IAuditLogProperties;

/** How an id list is compared, or undefined when the array is not a list of ids. */
export const getAuditLogIdCollectionConfig = ({
  path,
  key,
  values,
}: {
  path: string;
  key: string;
  values: unknown[];
}): IAuditLogIdCollectionConfig | undefined => {
  if (!values.every(isAuditLogId)) {
    return undefined;
  }
  return (
    AUDIT_LOG_ID_COLLECTIONS[path] ??
    (key.endsWith(AUDIT_LOG_ID_LIST_KEY_SUFFIX)
      ? { isOrdered: false }
      : undefined)
  );
};

export const isAuditLogItemCollectionOrdered = (path: string): boolean =>
  AUDIT_LOG_ORDERED_ITEM_COLLECTIONS.has(path);

export const hasAuditLogItemIds = (
  values: unknown[],
): values is { id: number | string }[] =>
  values.length > 0 &&
  values.every(
    item => isPlainObject(item) && isAuditLogId((item as TAuditLogRecord).id),
  );

const formatAuditLogItemLabel = ({
  id,
  name,
}: {
  id: string;
  name?: string;
}): string => (name ? `${name} (${id})` : id);

/** A display name for an item with an id: a station name, a passenger's full name or code. */
export const getAuditLogItemLabel = (item: TAuditLogRecord): string => {
  const fullName = [item.firstName, item.lastName]
    .filter(part => typeof part === "string" && part)
    .join(" ");
  const name = [item.name, fullName, item.code].find(
    candidate => typeof candidate === "string" && candidate,
  ) as string | undefined;

  return formatAuditLogItemLabel({ id: String(item.id), name });
};

/**
 * Names for ids, read from the ride's passengersInfo / stationsInfo in any of the given states. Areas carry no name in
 * the ride, so theirs come from `areaNameById` (the company's areas).
 */
export const buildAuditLogItemLabels = ({
  states,
  areaNameById,
}: {
  states: IAuditLogProperties[];
  areaNameById?: Map<number, string>;
}): TAuditLogItemLabels => {
  const labels: TAuditLogItemLabels = {
    passengers: new Map(),
    units: new Map(),
    stations: new Map(),
    areas: new Map(
      [...(areaNameById ?? [])].map(([id, name]) => [
        String(id),
        formatAuditLogItemLabel({ id: String(id), name }),
      ]),
    ),
  };

  for (const state of states) {
    const metadata = state?.[METADATA_KEY] as TAuditLogRecord | undefined;
    const passengersInfo = metadata?.passengersInfo;
    const stationsInfo = metadata?.stationsInfo;

    if (Array.isArray(passengersInfo)) {
      for (const passenger of passengersInfo as TAuditLogRecord[]) {
        if (!isAuditLogId(passenger?.id)) {
          continue;
        }
        labels.passengers.set(
          String(passenger.id),
          getAuditLogItemLabel(passenger),
        );
        if (
          isAuditLogId(passenger.unitId) &&
          typeof passenger.unitName === "string" &&
          passenger.unitName
        ) {
          labels.units.set(
            String(passenger.unitId),
            formatAuditLogItemLabel({
              id: String(passenger.unitId),
              name: passenger.unitName,
            }),
          );
        }
      }
    }

    if (Array.isArray(stationsInfo)) {
      for (const station of stationsInfo as TAuditLogRecord[]) {
        if (isAuditLogId(station?.id)) {
          labels.stations.set(
            String(station.id),
            getAuditLogItemLabel(station),
          );
        }
      }
    }
  }

  return labels;
};

export const getAuditLogIdLabel = ({
  id,
  labelSource,
  itemLabels,
}: {
  id: string;
  labelSource?: TAuditLogItemLabelSource;
  itemLabels?: TAuditLogItemLabels;
}): string => (labelSource && itemLabels?.[labelSource].get(id)) || id;

/** Which ids came and went, and for an ordered collection whether the ids kept on both sides moved. */
export const diffAuditLogItemIds = ({
  before,
  after,
  isOrdered,
}: {
  before: string[];
  after: string[];
  isOrdered: boolean;
}): { added: string[]; removed: string[]; isReordered: boolean } => {
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  const added = after.filter(id => !beforeSet.has(id));
  const removed = before.filter(id => !afterSet.has(id));
  const isReordered =
    isOrdered &&
    !isEqual(
      before.filter(id => afterSet.has(id)),
      after.filter(id => beforeSet.has(id)),
    );

  return { added, removed, isReordered };
};

/**
 * Update rows written before the current diff version dropped removed array items and keys, and a history without its creation
 * row starts from unknown values. Both are shown as a notice above the history.
 */
export const getAuditLogHistoryGaps = (
  auditLogs: Pick<IAuditLogDto, "eventName" | "metadata">[],
): { hasRowsWithoutRemovals: boolean; isMissingCreation: boolean } => ({
  hasRowsWithoutRemovals: auditLogs.some(
    log =>
      log.eventName === AuditLogEvents.EntityUpdated &&
      (log.metadata?.diffVersion ?? 0) < AUDIT_LOG_DIFF_VERSION,
  ),
  isMissingCreation:
    auditLogs.length > 0 &&
    !auditLogs.some(log => log.eventName === AuditLogEvents.EntityCreated),
});
